/**
 * Shared date range utilities for period-based filtering.
 * Used by Analytics and Activity for consistent period logic (Daily, Weekly, Monthly, Yearly, All time).
 */

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Set date range based on period type and offset.
 * Mirrors the logic in useAnalyticsHelpers for consistency.
 */
export function setDateRangeByPeriod(
  periodType: string,
  offset: number,
  setFromDate: (date: string) => void,
  setToDate: (date: string) => void,
  workspaceCreatedAt?: Date | null
): void {
  const today = new Date();
  let from: Date;
  let to: Date;

  switch (periodType) {
    case "All time": {
      from = workspaceCreatedAt || new Date(2000, 0, 1);
      to = today;
      break;
    }
    case "Daily": {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + offset);
      from = new Date(targetDate);
      to = new Date(targetDate);
      break;
    }
    case "Weekly": {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + offset * 7);
      const dayOfWeek = targetDate.getDay();
      from = new Date(targetDate);
      from.setDate(targetDate.getDate() - dayOfWeek); // Start of week (Sunday)
      to = new Date(from);
      to.setDate(from.getDate() + 6); // End of week (Saturday)
      break;
    }
    case "Monthly": {
      const targetDate = new Date(today);
      targetDate.setMonth(today.getMonth() + offset);
      from = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
      to = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
      break;
    }
    case "Yearly": {
      const targetDate = new Date(today);
      targetDate.setFullYear(today.getFullYear() + offset);
      from = new Date(targetDate.getFullYear(), 0, 1);
      to = new Date(targetDate.getFullYear(), 11, 31);
      break;
    }
    default:
      return;
  }

  setFromDate(formatLocalDate(from));
  setToDate(formatLocalDate(to));
}
